// PPCRequests.js — Edit Requests (rebuilt clean)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PPC_API_URL } from './PPCContext';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const DASH_COLORS = { bravo:'#d32f2f', papa:'#059669', romeo:'#7b1fa2', golf:'#e65100' };
const STATUS_STYLES = {
  pending:  { bg:'#fff8e1', color:'#f9a825' },
  approved: { bg:'#e8f5e9', color:'#2e7d32' },
  rejected: { bg:'#ffebee', color:'#c62828' },
};

const fmt = (d) => d ? new Date(d).toLocaleString('en-IN',{ day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

// ─────────────────────────────────────────────────────────────────────────────
export default function PPCRequests() {
  const navigate = useNavigate();

  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [status,     setStatus]     = useState('pending');
  const [dashFilter, setDashFilter] = useState('all');
  const [toast,      setToast]      = useState(null);
  const [counts,     setCounts]     = useState({ pending:0, approved:0, rejected:0 });

  const toast$ = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${PPC_API_URL}/edit_requests.php?action=list&status=${status}`;
      if (dashFilter !== 'all') url += `&dashboard=${dashFilter}`;
      const res = await axios.get(url, auth());
      if (res.data?.success) setRequests(res.data.requests || []);
    } catch (e) {
      toast$('Failed to load requests', 'error');
    }
    setLoading(false);
  }, [status, dashFilter]);

  // Fetch counts for tab badges
  const fetchCounts = useCallback(async () => {
    try {
      const [p, a, r] = await Promise.all([
        axios.get(`${PPC_API_URL}/edit_requests.php?action=list&status=pending`, auth()),
        axios.get(`${PPC_API_URL}/edit_requests.php?action=list&status=approved`, auth()),
        axios.get(`${PPC_API_URL}/edit_requests.php?action=list&status=rejected`, auth()),
      ]);
      setCounts({
        pending:  (p.data?.requests||[]).length,
        approved: (a.data?.requests||[]).length,
        rejected: (r.data?.requests||[]).length,
      });
    } catch (_) {}
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleApprove = async (id) => {
    try {
      const res = await axios.put(
        `${PPC_API_URL}/edit_requests.php?action=approve`,
        { request_id: id }, auth()
      );
      if (res.data?.success) { toast$('Request approved & applied!'); fetchRequests(); fetchCounts(); }
      else toast$(res.data?.error||'Failed to approve', 'error');
    } catch (e) { toast$(e.response?.data?.error||'Failed', 'error'); }
  };

  const handleReject = async (id) => {
    const reason = prompt('Rejection reason (optional):');
    try {
      const res = await axios.put(
        `${PPC_API_URL}/edit_requests.php?action=reject`,
        { request_id: id, reject_reason: reason||'' }, auth()
      );
      if (res.data?.success) { toast$('Request rejected'); fetchRequests(); fetchCounts(); }
      else toast$(res.data?.error||'Failed to reject', 'error');
    } catch (e) { toast$(e.response?.data?.error||'Failed', 'error'); }
  };

  // ── Parse old/new values ─────────────────────────────────────────────────
  const parseVals = (raw) => {
    if (!raw) return null;
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
    catch (_) { return { value: raw }; }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Header */}
      <header style={S.hdr}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button style={S.hbtn} onClick={() => navigate('/ppc')}>← Back</button>
          <span style={S.htitle}>📝 Edit Requests</span>
        </div>
        <button style={S.refreshBtn} onClick={() => { fetchRequests(); fetchCounts(); }}>
          🔄 Refresh
        </button>
      </header>

      {/* Status Tabs */}
      <div style={S.tabRow}>
        {['pending','approved','rejected'].map(s => (
          <button key={s} onClick={()=>setStatus(s)}
            style={{ ...S.tab, ...(status===s ? S.tabActive(s) : {}) }}>
            {s.toUpperCase()}
            {counts[s] > 0 && (
              <span style={{ ...S.badge, background: status===s ? '#fff' : '#e2e6ea', color: status===s ? (s==='pending'?'#d97706':s==='approved'?'#059669':'#d32f2f') : '#6b7280' }}>
                {counts[s]}
              </span>
            )}
          </button>
        ))}

        {/* Dashboard filter */}
        <select value={dashFilter} onChange={e=>setDashFilter(e.target.value)} style={S.sel}>
          <option value="all">All Dashboards</option>
          <option value="bravo">BRAVO</option>
          <option value="papa">PAPA</option>
          <option value="romeo">ROMEO</option>
          <option value="golf">GOLF</option>
        </select>
      </div>

      {/* Requests list */}
      <div style={{ padding:'0 24px 30px' }}>
        {loading ? (
          <div style={S.center}>Loading requests…</div>
        ) : requests.length === 0 ? (
          <div style={S.emptyBox}>
            <div style={{ fontSize:48, marginBottom:14 }}>📋</div>
            <div style={{ fontSize:16, fontWeight:600, color:'#374151', marginBottom:6 }}>
              No {status} requests
            </div>
            <div style={{ fontSize:13, color:'#9ca3af' }}>
              There are no {status} edit requests{dashFilter!=='all'?` for ${dashFilter.toUpperCase()}`:''}
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {requests.map(req => {
              const oldV = parseVals(req.old_values || req.old_value);
              const newV = parseVals(req.new_values || req.new_value);
              const dc   = DASH_COLORS[req.dashboard] || '#64748b';
              const sc   = STATUS_STYLES[req.status]  || STATUS_STYLES.pending;
              return (
                <div key={req.id} style={S.reqCard}>
                  {/* Card header */}
                  <div style={S.cardHdr}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ background:dc, color:'#fff', padding:'4px 12px', borderRadius:6, fontSize:12, fontWeight:700 }}>
                        {(req.dashboard||'').toUpperCase()}
                      </span>
                      <span style={{ ...sc, padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:600 }}>
                        {(req.status||'').toUpperCase()}
                      </span>
                      <span style={{ fontSize:11, color:'#6b7280' }}>#{req.id}</span>
                    </div>
                    <span style={{ fontSize:11, color:'#9ca3af' }}>{fmt(req.created_at)}</span>
                  </div>

                  {/* Card body */}
                  <div style={S.cardBody}>
                    {/* Info column */}
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {[
                        ['Requested by', req.requested_by_name || `User #${req.requested_by}`],
                        ['Contractor',   req.contractor],
                        ['Date',         req.entry_date],
                        ['Shift',        req.shift],
                        ['Type',         req.request_type?.toUpperCase()],
                      ].map(([label, val]) => val ? (
                        <div key={label} style={{ display:'flex', gap:8, fontSize:13 }}>
                          <span style={{ color:'#6b7280', minWidth:110, fontSize:12 }}>{label}:</span>
                          <span style={{ color:'#1a1d23', fontWeight:500 }}>{val}</span>
                        </div>
                      ) : null)}
                      {req.reason && (
                        <div style={{ marginTop:4, padding:'8px 12px', background:'#fef9c3', borderRadius:7, fontSize:12, color:'#713f12' }}>
                          💬 {req.reason}
                        </div>
                      )}
                    </div>

                    {/* Values column */}
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {oldV && (
                        <ValBox label="Current Values" vals={oldV} bg="#fff5f5" border="#fca5a5" />
                      )}
                      {newV && (
                        <ValBox label="Requested Changes" vals={newV} bg="#f0fdf4" border="#86efac" />
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {req.status === 'pending' && (
                    <div style={S.cardActions}>
                      <button onClick={()=>handleApprove(req.id)} style={S.approveBtn}>
                        ✓ Approve & Apply
                      </button>
                      <button onClick={()=>handleReject(req.id)} style={S.rejectBtn}>
                        ✕ Reject
                      </button>
                    </div>
                  )}

                  {req.status !== 'pending' && req.reviewed_at && (
                    <div style={{ padding:'10px 16px', borderTop:'1px solid #e2e6ea', fontSize:11, color:'#9ca3af', textAlign:'center' }}>
                      Reviewed {fmt(req.reviewed_at)}
                      {req.admin_notes && <span style={{ marginLeft:8, color:'#6b7280' }}>· {req.admin_notes}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {toast && (
        <div style={{ position:'fixed', bottom:22, right:22, padding:'11px 20px', borderRadius:9,
          background:toast.type==='error'?'#d32f2f':'#059669',
          color:'#fff', fontSize:13, fontWeight:500, zIndex:9999, boxShadow:'0 4px 18px rgba(0,0,0,.25)' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ── ValBox ────────────────────────────────────────────────────────────────────
const ValBox = ({ label, vals, bg, border }) => (
  <div style={{ background:bg, border:`1px solid ${border}`, borderRadius:8, padding:'10px 14px', fontSize:12 }}>
    <div style={{ fontWeight:700, color:'#1e293b', marginBottom:8 }}>{label}</div>
    {typeof vals === 'object'
      ? Object.entries(vals).map(([k, v]) => (
        <div key={k} style={{ display:'flex', gap:6, marginBottom:4 }}>
          <span style={{ color:'#94a3b8', minWidth:120 }}>{k.replace(/_/g,' ')}:</span>
          <span style={{ color:'#1e293b', fontWeight:500 }}>{String(v??'—')}</span>
        </div>
      ))
      : <span>{String(vals)}</span>
    }
  </div>
);

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:       { minHeight:'100vh', background:'#f0f2f5', fontFamily:"'DM Sans',sans-serif", fontSize:13 },
  hdr:        { background:'linear-gradient(135deg,#d32f2f,#1565c0)', padding:'0 24px', height:56, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, boxShadow:'0 2px 12px rgba(211,47,47,.3)' },
  hbtn:       { background:'rgba(255,255,255,.18)', border:'1px solid rgba(255,255,255,.3)', color:'#fff', padding:'6px 14px', borderRadius:20, cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:500 },
  htitle:     { color:'#fff', fontSize:16, fontWeight:700 },
  refreshBtn: { background:'rgba(255,255,255,.18)', border:'1px solid rgba(255,255,255,.3)', color:'#fff', padding:'6px 14px', borderRadius:8, cursor:'pointer', fontSize:12, fontFamily:'inherit', fontWeight:600 },
  tabRow:     { display:'flex', gap:8, padding:'16px 24px', background:'#fff', borderBottom:'1px solid #e2e6ea', alignItems:'center', flexWrap:'wrap' },
  tab:        { padding:'8px 18px', border:'1px solid #e2e6ea', borderRadius:20, background:'#fff', cursor:'pointer', fontSize:12, fontWeight:600, color:'#6b7280', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6, transition:'all .15s' },
  tabActive:  (s) => ({
    background: s==='pending'?'#fff8e1':s==='approved'?'#e8f5e9':'#ffebee',
    color:      s==='pending'?'#b45309':s==='approved'?'#15803d':'#b91c1c',
    border:     `1px solid ${s==='pending'?'#fcd34d':s==='approved'?'#86efac':'#fca5a5'}`,
  }),
  badge:      { padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 },
  sel:        { marginLeft:'auto', padding:'8px 12px', border:'1px solid #e2e6ea', borderRadius:8, fontFamily:'inherit', fontSize:12, background:'#fff', cursor:'pointer' },
  center:     { padding:'60px', textAlign:'center', color:'#9ca3af', fontSize:13, background:'#fff', borderRadius:14, margin:'24px' },
  emptyBox:   { padding:'60px', textAlign:'center', background:'#fff', borderRadius:14, border:'1px solid #e2e6ea' },
  reqCard:    { background:'#fff', borderRadius:12, border:'1px solid #e2e6ea', overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,.05)' },
  cardHdr:    { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', background:'#f8f9fb', borderBottom:'1px solid #e8ecf0', flexWrap:'wrap', gap:8 },
  cardBody:   { padding:'16px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 },
  cardActions:{ display:'flex', gap:12, padding:'14px 16px', borderTop:'1px solid #e2e6ea', background:'#fafafa' },
  approveBtn: { flex:1, padding:'11px', border:'none', borderRadius:8, background:'linear-gradient(135deg,#15803d,#16a34a)', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer' },
  rejectBtn:  { flex:1, padding:'11px', border:'none', borderRadius:8, background:'linear-gradient(135deg,#b91c1c,#dc2626)', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer' },
};
